import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Heart, CheckCircle, Loader2, Share2, Home, XCircle } from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const SupportUsSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError('No session ID provided');
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/platform-donations/checkout-status/${sessionId}`);
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      setError('Failed to verify payment status');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'I just supported FundTheHope!',
        text: 'Join me in supporting this amazing crowdfunding platform that keeps 0% fees!',
        url: window.location.origin + '/support-us'
      });
    } else {
      navigator.clipboard.writeText(window.location.origin + '/support-us');
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="py-20">
          <div className="max-w-lg mx-auto text-center">
            <Loader2 className="w-12 h-12 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-muted-foreground">Verifying your donation...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || status?.payment_status !== 'paid') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="py-20">
          <div className="max-w-lg mx-auto px-4">
            <Card className="bg-card border-border">
              <CardContent className="pt-8 text-center">
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Payment Not Completed</h1>
                <p className="text-muted-foreground mb-6">
                  {error || 'Your payment was not completed. Please try again.'}
                </p>
                <Button onClick={() => navigate('/support-us')} className="bg-emerald-500">
                  Try Again
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="py-20">
        <div className="max-w-lg mx-auto px-4">
          <Card className="bg-card border-border overflow-hidden">
            {/* Success Animation Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 py-12 px-6 text-center relative overflow-hidden">
              <div className="absolute inset-0 opacity-20">
                {[...Array(20)].map((_, i) => (
                  <Heart
                    key={i}
                    className="absolute text-white animate-pulse"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                      fontSize: `${Math.random() * 20 + 10}px`,
                      animationDelay: `${Math.random() * 2}s`
                    }}
                  />
                ))}
              </div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <CheckCircle className="w-12 h-12 text-emerald-500" />
                </div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Thank You!</h1>
                <p className="text-white/80">Your generosity makes a difference</p>
              </div>
            </div>

            <CardContent className="pt-8 text-center">
              {/* Donation Details */}
              <div className="bg-secondary/50 rounded-xl p-6 mb-6">
                <p className="text-sm text-muted-foreground mb-1">
                  {status?.donation_type === 'monthly' ? 'Monthly Donation' : 'One-Time Donation'}
                </p>
                <p className="text-4xl font-bold text-foreground">
                  ${status?.amount}
                  {status?.donation_type === 'monthly' && <span className="text-lg font-normal">/mo</span>}
                </p>
                {status?.tier && (
                  <p className="text-sm text-emerald-400 mt-2">
                    {status.tier.charAt(0).toUpperCase() + status.tier.slice(1)} Tier
                  </p>
                )}
                {!status?.is_anonymous && status?.donor_name && (
                  <p className="text-muted-foreground mt-2">From {status.donor_name}</p>
                )}
              </div>

              {/* Message */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-foreground mb-2">You're Amazing! 💜</h2>
                <p className="text-muted-foreground text-sm">
                  Your support helps us maintain 0% platform fees and serve more communities in need. 
                  A confirmation email has been sent to your inbox.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={handleShare} variant="outline" className="flex-1">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                <Button onClick={() => navigate('/')} className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-600">
                  <Home className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SupportUsSuccessPage;
